// Attachment helpers for the StackIQ composer. Pure logic (validation,
// base64 handling) is separated from the browser-only bits (FileReader,
// canvas resize) so the pure parts are unit-testable under jsdom.

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const ACCEPTED_TYPES = [...IMAGE_TYPES, 'application/pdf', 'text/plain', 'text/csv', 'text/markdown'];
export const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(',');
export const MAX_ATTACHMENTS = 5;
export const MAX_BYTES = 5 * 1024 * 1024;
// Longest-edge cap for images (Anthropic's effective resolution ceiling).
export const MAX_IMAGE_DIMENSION = 1568;

export type AiAttachmentPayload = { name: string; media_type: string; data: string };

export const isImageType = (type: string) => IMAGE_TYPES.includes(type);

// Pre-read validation (size of non-images; images get resized so they are
// checked after conversion instead).
export const validateFile = (file: { type: string; size: number }): string | null => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return 'Unsupported file type. Use images, PDF, or plain text/CSV.';
  }
  if (!isImageType(file.type) && file.size > MAX_BYTES) {
    return 'Files must be 5MB or smaller.';
  }
  return null;
};

// "data:<type>;base64,AAAA" -> "AAAA"
export const dataUrlToBase64 = (dataUrl: string): string => {
  const index = dataUrl.indexOf('base64,');
  return index === -1 ? dataUrl : dataUrl.slice(index + 'base64,'.length);
};

export const base64Bytes = (data: string): number => Math.floor(data.length * 0.75);

export const scaleDimensions = (
  width: number,
  height: number,
  max: number = MAX_IMAGE_DIMENSION
): { width: number; height: number } => {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };
  const ratio = max / longest;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
};

// --- Browser-only below (FileReader / canvas) ---

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const resizeImage = (dataUrl: string, type: string): Promise<string> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const { width, height } = scaleDimensions(image.width, image.height);
      if (width === image.width && height === image.height) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(dataUrl);
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL(type === 'image/gif' ? 'image/png' : type, 0.9));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });

// File -> payload; returns an error string instead when invalid.
export const fileToAttachment = async (file: File): Promise<AiAttachmentPayload | string> => {
  const validationError = validateFile(file);
  if (validationError) return validationError;

  let dataUrl = await readAsDataUrl(file);
  if (isImageType(file.type)) {
    dataUrl = await resizeImage(dataUrl, file.type);
  }
  const data = dataUrlToBase64(dataUrl);
  if (base64Bytes(data) > MAX_BYTES) return 'Files must be 5MB or smaller.';

  // A resized gif comes back as png — reflect the actual payload type.
  const mediaTypeMatch = /^data:([^;]+);/.exec(dataUrl);
  return {
    name: file.name || 'attachment',
    media_type: mediaTypeMatch ? mediaTypeMatch[1] : file.type,
    data,
  };
};
